// Traducción inglesa de los problemas. Overrides por slug; no incluir
// ogImage, services ni relatedGuides (se heredan del español).
export const problemsEn = [
  {
    slug: "catastro-escritura-no-coinciden",
    shortName: "Catastro and deed don't match",
    seoTitle: "Catastro and title deed don't match: what to do | MideFinca",
    metaDescription:
      "If Catastro and your deed state different surface areas, the first step is knowing which one departs from reality. We explain the causes and how to check it with a technical survey.",
    excerpt: "Two documents, two different figures. Reality has only one: it gets measured.",
    h1: "Catastro and title deed don't match",
    answer:
      "Catastro and the deed attributing different surface areas to the same plot is very frequent, especially in older properties. Neither figure is automatically correct: the objective reference is the reality on the ground, and that is only known by measuring. Until you measure, any conclusion is a guess.",
    causes: [
      "Old deeds granted 'by apparent extent': the surface area was declared without real measurement.",
      "Historical cadastral mapping surveyed with less precision than today's.",
      "Segregations, strip purchases or boundary changes documented in only one of the sources.",
      "Digitisation or graphic representation errors in Catastro.",
    ],
    checks: [
      "Real measured surface area of the plot, with georeferenced coordinates.",
      "Cadastral graphic representation: where Catastro draws the parcel and with what geometry.",
      "History of the title: previous deeds, segregations or groupings.",
      "State of the physical boundaries: walls, fences, markers.",
    ],
    professional:
      "The measurement and diagnosis belong to a technician competent in surveying and mapping. If the title needs correcting or a different surface area needs registering, a notary and the Land Registry are also involved; amending cadastral data is up to the Catastro office.",
  },
  {
    slug: "no-se-donde-termina-mi-finca",
    shortName: "I don't know where my plot ends",
    seoTitle: "I don't know where my plot's boundaries are | MideFinca",
    metaDescription:
      "If you don't know where your plot's boundaries start and end, the solution is to measure it and cross-check it with the documentation. We explain how it is verified.",
    excerpt: "Moved walls, vanished markers, ambiguous boundaries. Limits are measured, not guessed.",
    h1: "I don't know where my plot's boundaries are",
    answer:
      "It is one of the most frequent enquiries: the deed describes adjoining boundaries from another era ('to the north, lands of…'), the markers have disappeared and the current fence may not match the documentary limit. The check starts by measuring the plot and comparing the resulting geometry with the deed and with Catastro.",
    causes: [
      "Plot described by old adjoining boundaries, without defined geometry.",
      "Disappearance of boundary stones and markers due to works, vegetation or time.",
      "Fences and walls built by approximation, not on the documentary limit.",
      "Inheritances and partial sales never set out again on the ground.",
    ],
    checks: [
      "Measurement of the plot and of the existing physical boundary features.",
      "Comparison of the measured geometry with the deed extent and the cadastral representation.",
      "State of the neighbouring plots and their graphic representations.",
      "Existence of markers or remains of old demarcation.",
    ],
    professional:
      "The technical location of the limits belongs to a surveying technician. If the boundary is disputed with a neighbour and there is no agreement, judicial demarcation is up to the courts, with lawyers and court-appointed experts involved.",
  },
  {
    slug: "mi-finca-tiene-mas-o-menos-metros",
    shortName: "My plot has more or fewer metres than Catastro says",
    seoTitle: "My plot has more or fewer metres than Catastro says | MideFinca",
    metaDescription:
      "Do you suspect your plot has more or fewer square metres than Catastro or the deed show? We explain why it happens and how to know the real surface area.",
    excerpt: "Doubt is not solved with more documents: it is solved by measuring.",
    h1: "My plot has more or fewer square metres than the paperwork says",
    answer:
      "Whether you suspect your plot has more square metres than declared or you think some are missing, the path is the same: measure. A georeferenced survey gives you the real surface area and allows an objective comparison with Catastro and the deed, to know which source departs from reality and by how much.",
    causes: [
      "If it seems to have more: conservative deeds by apparent extent, old cadastral survey errors, or strips gained by boundary moves never documented.",
      "If it seems to have fewer: partial sales or transfers not reflected in all registers, encroachment by neighbours, or a cadastral representation that includes someone else's land.",
      "In both cases: differences in measurement criteria between eras and sources.",
    ],
    checks: [
      "Real surface area measured on the ground.",
      "Comparison with the cadastral and registered surface areas, figure by figure.",
      "Review of the plot's documentary history.",
      "Check that the physical boundaries match the documentary geometry.",
    ],
    professional:
      "Determining the real surface area is technical surveying work. The consequences (correcting Catastro, updating the title, claiming against a third party) may also require a notary, the Land Registry or legal advice, depending on the case.",
  },
  {
    slug: "necesito-gml",
    shortName: "I need a GML file",
    seoTitle: "I've been asked for a GML file: what it is and how to get one | MideFinca",
    metaDescription:
      "If the notary or the Land Registry asks you for a GML file of your plot, we explain what it is, why they require it and how it is produced from a real measurement.",
    excerpt: "The notary asks for it, the Land Registry requires it. This is how a valid GML is produced.",
    h1: "I've been asked for a GML file of my plot",
    answer:
      "The GML is a computer file with your plot's georeferenced geometry: the official way to 'draw' the property on the mapping. Notaries and the Land Registry have required it since Act 13/2015 for sales, inheritances, segregations and declarations of new construction. It is not drawn over an aerial photo: it is built from measured coordinates and validated with Catastro's tools.",
    causes: [
      "Sale, inheritance, donation or contribution of the plot.",
      "Segregation, division or grouping of parcels.",
      "Declaration or extension of new construction.",
      "Incorporation of the georeferenced graphic representation into the deed or the Land Registry.",
    ],
    checks: [
      "What geometry the GML must contain: parcel, construction or both.",
      "Whether the current cadastral representation matches reality (if not, it is worth measuring first).",
      "Validity of the file in Catastro's official tools.",
      "Consistency of the geometry with the deed and the neighbouring plots.",
    ],
    professional:
      "Producing the GML from a real measurement is technical work. Incorporating the file into the file and its effects belong to the notary and the registrar handling your case.",
  },
  {
    slug: "comprar-finca-comprobar-superficie",
    shortName: "Buying a plot and checking the metres",
    seoTitle: "Buying a plot: how to check the square metres before signing | MideFinca",
    metaDescription:
      "Before buying a plot in the Canary Islands, check the real surface area, the boundaries and the consistency with Catastro and the deed. That way you avoid paying for metres that don't exist.",
    excerpt: "The best negotiation tool when buying a plot is a measurement.",
    h1: "I want to buy a plot and check the square metres first",
    answer:
      "It is the most sensible decision. The listing, the deed and Catastro can state three different figures, and the one that matters when paying is the real one. A prior check measures the plot, verifies its boundaries and compares everything with the documentation, before you sign and the surprises become yours.",
    causes: [
      "Listing surface areas taken from Catastro without verification.",
      "Old deeds by apparent extent that the seller takes for granted.",
      "Physical boundaries that don't match the documentary ones.",
      "Undeclared constructions the buyer would inherit as a problem.",
    ],
    checks: [
      "Measurement of the parcel's real surface area.",
      "Consistency between reality, Catastro and deed.",
      "State of the boundaries and possible encroachment by neighbours.",
      "Existence of constructions and whether they are documented.",
    ],
    professional:
      "The technical check before buying belongs to a surveying technician. If the report detects complex documentary problems, your lawyer or the notary assess how they affect the transaction.",
  },
  {
    slug: "construccion-no-documentada",
    shortName: "My construction doesn't appear in the deed",
    seoTitle: "My construction or extension doesn't appear in the deed | MideFinca",
    metaDescription:
      "If your home, extension or building doesn't appear in the deed or Catastro, we explain what technical documentation you need for the declaration of new construction.",
    excerpt: "The construction exists; the paperwork doesn't know. This is how it gets documented.",
    h1: "My construction or extension is not correctly documented",
    answer:
      "Whole dwellings, second floors, enclosed terraces, annexes…: it is very common for the built reality to exceed what the deed and Catastro say. The usual channel to document what is built is the declaration (or extension) of new construction before a notary, and its basis is technical: measurement, plans, descriptive certificate and, usually, a georeferenced graphic representation.",
    causes: [
      "Constructions made without a later deed recording them.",
      "Extensions carried out years ago and never declared.",
      "Inheritances of properties whose documentation was never updated.",
      "Buildings reflected in Catastro but not in the title, or the other way round.",
    ],
    checks: [
      "What exactly is built: surface area, footprint, position on the plot.",
      "Provable age of the construction: historical aerial photos, period documents.",
      "Planning status of the land, which conditions the options.",
      "What currently appears in Catastro and in the Land Registry.",
    ],
    professional:
      "The measurement, plans and technical certificate belong to the technician; the declaration of new construction is signed before a notary and registered in the Land Registry. The age and the planning status may require a specific certificate and additional advice. Every case has a different viability: it must be studied before promising anything.",
  },
{
    slug: "dividir-finca-en-parcelas",
    shortName: "I want to divide a plot into two or more parcels",
    seoTitle: "I want to divide a plot into two or more parcels | MideFinca",
    metaDescription:
      "Before segregating a plot you must check its registry, cadastral and planning situation and correctly define the new parcels. We explain what is reviewed.",
    excerpt: "Before dividing, the registry, cadastral and planning situation of the plot must be checked.",
    h1: "I want to divide a plot into two or more parcels",
    answer:
      "Dividing a plot is possible in many cases, but it is not automatic: segregation is conditional on planning regulations and on the authorisations that may be necessary. Before raising it, the registry, cadastral and planning situation of the plot must be checked and the new parcels must be technically defined: geometry, surface area and coordinates of each one.",
    causes: [
      "Inheritances that must be distributed among several heirs.",
      "Sale of only part of the plot.",
      "Distribution of land between partners or relatives.",
      "Plots too large for the intended use of one part.",
    ],
    checks: [
      "Registry situation: ownership, charges and description of the plot.",
      "Cadastral situation: current surface area and graphic representation.",
      "Planning regulations: whether the segregation is possible and under what conditions.",
      "Technical definition of the new parcels before any deed.",
    ],
    professional:
      "The technical definition of the new parcels and the graphic representation belong to the technician. The deed of division is notarial, the registration of the resulting properties is registry work and the planning authorisation, when necessary, belongs to the town hall. Catastro updates its representation based on what is documented.",
  },
  {
    slug: "unir-varias-fincas",
    shortName: "I want to join several plots into one",
    seoTitle: "I want to join several plots into one | MideFinca",
    metaDescription:
      "Grouping plots requires checking adjacency, ownership and documentation, and defining the geometry of the resulting plot. We explain what is reviewed before grouping.",
    excerpt: "Joining plots is not adding surface areas: the resulting plot must be defined and documented.",
    h1: "I want to join several plots into one",
    answer:
      "Joining two or more plots into one is a grouping (agrupación), and it requires technically defining the resulting plot: its new geometry, its surface area and its graphic representation. Not all plots can be grouped automatically: they must be adjoining, and their ownership, charges and the applicable regulations must be reviewed before confirming viability.",
    causes: [
      "Purchase of the neighbouring plot to join it to yours.",
      "Several inherited parcels you want to manage as one.",
      "Need for a single plot for a project or a transaction.",
      "Simplifying the documentation of several properties.",
    ],
    checks: [
      "Adjacency and compatibility of the parcels to be grouped.",
      "Ownership and registered charges of each plot.",
      "Consistency between deeds, Catastro and physical reality.",
      "Geometry and surface area of the resulting plot.",
    ],
    professional:
      "The measurement, the definition of the resulting plot and the graphic representation are technical work. The grouping is normally formalised by deed before a notary and registered at the Land Registry; Catastro is updated afterwards.",
  },
  {
    slug: "primera-ocupacion-fuera-de-ordenacion",
    shortName: "I need the first occupation of a non-conforming building",
    seoTitle: "First occupation of a non-conforming building | MideFinca",
    metaDescription:
      "If your building is fuera de ordenación (legally non-conforming) and you need to process its first occupation, the planning regime must be studied and the current state documented. We explain how.",
    excerpt: "The procedure requires documenting what exists, since when and in what conditions the building is.",
    h1: "I need the first occupation of a building in fuera de ordenación status",
    answer:
      "When a building is fuera de ordenación (legally non-conforming with current planning rules), processing its first occupation or use requires first studying the property's planning regime to check whether the procedure is possible, and then technically documenting the current state of the building, its age and its conditions. The decision always belongs to the competent town hall.",
    causes: [
      "Buildings constructed under earlier regulations or without them.",
      "Properties that need to accredit their conditions to contract or regularise utilities.",
      "Sales in which the building's situation must be accredited.",
      "Municipal files requiring documentation of the current state.",
    ],
    checks: [
      "The property's planning regime and whether it allows the procedure.",
      "The building's age and its evidence.",
      "Current state: measurement, plans and conditions.",
      "Habitability documentation, for dwellings and where applicable.",
    ],
    professional:
      "The study, measurement, plans and technical reports or certificates are our part. First occupation is resolved by the town hall according to planning regulations; we cannot guarantee its granting.",
  },
  {
    slug: "acreditar-uso-consolidado",
    shortName: "I need to prove my property's use is consolidated",
    seoTitle: "I need to prove the consolidated use of my property | MideFinca",
    metaDescription:
      "Accrediting a consolidated use (uso consolidado) requires demonstrating with evidence what use the building has, since when and in what conditions. We explain how it is documented.",
    excerpt: "A consolidated use is proven with evidence: age, characteristics and conditions of the current use.",
    h1: "I need to accredit the consolidated use of a property",
    answer:
      "To accredit a uso consolidado (consolidated use) you must demonstrate with evidence what use the building has, since when that use has existed and in what conditions it takes place. A technical report documents that reality objectively; the administrative recognition of the use, when necessary, belongs to the Administration according to the applicable planning regulations.",
    causes: [
      "The real use of the property does not match the one in the documentation.",
      "An administration or a procedure asks for accreditation of the consolidated use.",
      "Sales or regularisations in which the current use must be documented.",
      "Uses that have been carried out for years without sufficient documentary record.",
    ],
    checks: [
      "Current use of the building and its characteristics.",
      "Age of the use: historical aerial photography and period documentation.",
      "Conditions of the property and plans of the current state, where applicable.",
      "Planning regulations applicable to the specific case.",
    ],
    professional:
      "The technical documentation of the existing use is our part: report, measurement, plans and evidence. The planning assessment and the recognition of the consolidated use belong to the competent Administration.",
  },
  {
    slug: "que-puedo-construir-en-parcela",
    shortName: "I want to know what I can build on a parcel",
    seoTitle: "What can I build on a parcel: how to find out | MideFinca",
    metaDescription:
      "Before buying or designing it is advisable to know the land classification, permitted uses and building parameters. The cédula urbanística is the official document accrediting them.",
    excerpt: "What you can build is defined by planning: land classification, uses and parameters.",
    h1: "I want to know what I can build or do on a parcel",
    answer:
      "What can be built on a parcel is defined by the municipal planning rules: the land classification and categorisation, the permitted uses and the building parameters. The official document accrediting this is the cédula urbanística (planning information certificate), issued by the town hall. We prepare and process the application with the parcel's technical documentation.",
    causes: [
      "Buying a parcel without knowing its real possibilities.",
      "Projects you want to raise without confirming the planning conditions.",
      "Doubts about permitted uses or building parameters.",
      "Requests from notaries, banks or administrations asking for official planning information.",
    ],
    checks: [
      "Land classification and categorisation of the parcel.",
      "Permitted uses and building conditions.",
      "Planning parameters: occupancy, buildability, heights, setbacks.",
      "Consistency between the planning information and the physical reality of the land.",
    ],
    professional:
      "The official planning information is issued by the town hall. Our part is to correctly identify and document the parcel, prepare and submit the application, and explain the content of the municipal reply in clear terms.",
  },
  {
    slug: "alineaciones-y-rasantes",
    shortName: "I've been asked for the alineaciones y rasantes of a parcel",
    seoTitle: "I've been asked for the alineaciones y rasantes of a parcel | MideFinca",
    metaDescription:
      "If you are asked for the alineaciones y rasantes (building line and official levels) of a parcel, you need precise topographic documentation and, where applicable, the municipal resolution. We explain what they are and how they are obtained.",
    excerpt: "The alineación marks how far the building may reach; the rasante sets its reference level.",
    h1: "I've been asked for the alineaciones y rasantes of a parcel",
    answer:
      "The alineaciones y rasantes define the position and level that condition the future building in relation to the public road: the alineación is the building line and the rasante is the official reference level. When the file requires it, they are officially issued by the town hall; our part is the technical and topographic documentation: the precise survey of the parcel and the road, the plans and the preparation of the application.",
    causes: [
      "New building projects that must respect the official building line.",
      "Licence procedures requiring the alineaciones y rasantes.",
      "Parcels bordering the public road without a clear definition of their position relative to it.",
      "Doubts about the level at which to build.",
    ],
    checks: [
      "Topographic survey of the parcel and its surroundings in relation to the road.",
      "Existing municipal documentation on previous building lines.",
      "Levels of the road and the land to define the rasante.",
      "The municipal procedure applicable to the case.",
    ],
    professional:
      "The topographic survey and the technical documentation are our work. The official certification of alineaciones y rasantes, when the procedure requires it, is issued by the town hall.",
  },
  {
    slug: "que-permiso-necesito-reforma",
    shortName: "I want to renovate and don't know what permit I need",
    seoTitle: "I want to renovate and don't know what permit I need | MideFinca",
    metaDescription:
      "Prior notification or works licence: which procedure applies depends on the type of work and each municipality's ordinance. We explain how it is checked.",
    excerpt: "Not every small work qualifies for prior notification and not every renovation needs a licence: it depends on the municipal ordinance.",
    h1: "I want to renovate and I don't know what permit I need",
    answer:
      "The procedure applicable to a renovation depends on the type of work and on each town hall's ordinance: some works can be processed by comunicación previa (prior notification) and others require a planning licence with a technical project. Checking this before starting avoids filing the wrong procedure and its consequences.",
    causes: [
      "Renovations of homes or premises without knowing what procedure the municipality requires.",
      "Works that may or may not affect structural elements or the façade.",
      "Works started without a permit that must be regularised.",
      "Municipal requirements asking for technical documentation for the renovation.",
    ],
    checks: [
      "Type of work and whether it affects the structure, façade or layout.",
      "Applicable municipal ordinance: prior notification or licence.",
      "Technical documentation required by the corresponding procedure.",
      "Whether the work needs a technical project or a simple report is enough.",
    ],
    professional:
      "Checking the procedure and producing the technical documentation is our part: report, plans, measurement and, when the works require it, a project. The authorisation, in either procedure, belongs to the town hall.",
  },
  {
    slug: "obra-con-licencia-municipal",
    shortName: "I'm going to build and need a municipal licence",
    seoTitle: "I'm going to build and need a municipal licence | MideFinca",
    metaDescription:
      "New construction, extensions, refurbishments or structural interventions: when the works require a major works licence, a technical project is needed. We explain what documentation is prepared.",
    excerpt: "When the works require a licence, the file needs a complete technical project.",
    h1: "I'm going to carry out works that need a municipal licence",
    answer:
      "Works that require a licencia de obra mayor (major works licence) need a technical project and prior municipal authorisation: depending on the case, new construction, extensions, certain renovations, refurbishments or structural interventions. The project and the file documentation are prepared before submitting the application, and the granting belongs to the town hall.",
    causes: [
      "Construction of a new building.",
      "Extension of an existing construction.",
      "Renovations affecting the structure or the configuration of the property.",
      "Refurbishments or structural interventions.",
    ],
    checks: [
      "Planning regulations applicable to the parcel and the works.",
      "Measurement and survey of the current state, where applicable.",
      "Scope of the technical project required according to the works.",
      "Documentation the town hall requires for the file.",
    ],
    professional:
      "The preliminary study, the measurement, the technical project and the submission of the application are our part. The licence is granted or denied by the town hall according to planning regulations; its granting cannot be guaranteed, but a technically well-prepared file can.",
  },
  {
    slug: "dividir-edificio-en-unidades",
    shortName: "I want to divide a building into independent units",
    seoTitle: "I want to divide a building into several units | MideFinca",
    metaDescription:
      "Dividing a building into independent flats or premises requires measuring it, describing each unit and setting its quotas. We explain what is reviewed before signing the deed.",
    excerpt: "Dividing a building into units requires measuring it and describing each part before the deed.",
    h1: "I want to divide a building into several independent units",
    answer:
      "Dividing a building into independent units is formalised through the horizontal division (división horizontal), and its technical side must be measured and properly described before signing the deed: floor area and layout of each unit, common elements and participation quotas. If the title is drafted on approximate data, the discrepancies appear later, during the sale or the distribution.",
    causes: [
      "Inheritances involving a building that must be shared among several heirs.",
      "Family buildings to be converted into independent dwellings.",
      "Small developments selling each unit separately.",
      "Old constitutive titles that no longer reflect the built reality.",
    ],
    checks: [
      "Real measurement of the building and of each unit, not only the old plans.",
      "Description of the common elements and of the share belonging to each unit.",
      "Participation quotas coherent with the measurement.",
      "Previous registry and cadastral situation of the property.",
    ],
    professional:
      "The measurement and technical documentation of the units correspond to the technician. The deed of horizontal division is notarial and its registration belongs to the Land Registry. If the existing constitutive title or statutes are affected, additional consents may be necessary depending on the case.",
    services: ["division-horizontal-canarias", "certificado-superficies"],
    relatedGuides: ["que-es-division-horizontal"],
  },
  {
    slug: "convertir-local-en-vivienda",
    shortName: "I want to convert commercial premises into a dwelling",
    seoTitle: "I want to convert commercial premises into a dwelling | MideFinca",
    metaDescription:
      "Not all premises can be converted into a dwelling: it depends on the planning rules, the municipal ordinance and the conditions of the premises themselves. We explain what is checked first.",
    excerpt: "Before building or buying, you must check whether the premises and the rules allow the change of use.",
    h1: "I want to convert commercial premises into a dwelling",
    answer:
      "Converting commercial premises into a dwelling is not just a refurbishment: it is a change of use that must be allowed by the planning rules and the municipal ordinance, and it requires the space to meet specific habitability conditions, such as natural light and ventilation or minimum ceiling heights. That is why the first step is not the works, but the feasibility study.",
    causes: [
      "Disused premises whose value would be higher as a dwelling.",
      "Purchases of ground-floor commercial units already intended for transformation.",
      "The need to make use of an owned space for residential use.",
      "Investment in consolidated areas where dwellings are in higher demand.",
    ],
    checks: [
      "Planning rules and municipal ordinance: whether the change of use is admissible on that plot or building.",
      "Physical conditions of the premises: openings to open space or courtyard, ventilation, clear height and layout.",
      "Statutes of the owners' community, if the premises are part of a horizontally divided building.",
      "Applicable procedure: prior notification, responsible declaration or permit, depending on the municipality and the scope of the works.",
    ],
    professional:
      "The feasibility study and the technical documentation correspond to the technician. The urban planning admissibility and the resolution of the procedure correspond to the town hall; when the premises are in a building under the horizontal property regime, the community statutes should also be reviewed.",
    services: ["cambio-de-uso-local-a-vivienda", "certificado-habitabilidad", "licencia-obra-mayor"],
    relatedGuides: ["cambio-de-uso-local-a-vivienda-canarias"],
  },
];
